import { Box, Container, Grid, Typography, Link as MuiLink, Divider, useTheme, Paper, Button, IconButton, Stack, alpha } from '@mui/material';
import {
  WhatsApp,
  LinkedIn,
  X,
  Instagram,
  Facebook,
  YouTube,
  Email,
  Phone,
  LocationOn,
  ArrowUpward,
  SendRounded,
  Business
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const FooterSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2),
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-8px)',
    '& .section-title': {
      background: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.secondary.light})`,
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    }
  }
}));

const SocialIconButton = styled(IconButton)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.08)})`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  color: theme.palette.primary.main,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    borderColor: alpha(theme.palette.primary.main, 0.4),
    color: theme.palette.primary.contrastText,
    transform: 'translateY(-5px) scale(1.1)',
    boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.3)}`
  }
}));

const LinkItem = styled(MuiLink)(({ theme }) => ({
  display: 'block',
  padding: theme.spacing(0.75, 0),
  transition: 'all 0.3s ease',
  fontWeight: 500,
  fontSize: '0.95rem',
  '&:hover': {
    transform: 'translateX(8px)',
    color: theme.palette.primary.main
  }
}));

const StyledInput = styled(Box)(({ theme }) => ({
  flex: 1,
  border: 'none',
  padding: theme.spacing(1.2),
  backgroundColor: 'transparent',
  color: theme.palette.text.primary,
  outline: 'none',
  '&::placeholder': {
    opacity: 0.7,
    color: theme.palette.text.secondary
  }
}));

const Footer = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const footerBackground = isDarkMode
    ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.primary.dark, 0.1)} 50%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`
    : `linear-gradient(135deg, ${alpha('#fff', 0.98)} 0%, ${alpha(theme.palette.primary.main, 0.03)} 50%, ${alpha('#fff', 0.98)} 100%)`;

  return (
    <Box
      component="footer"
      sx={{
        background: footerBackground,
        backdropFilter: 'blur(20px)',
        pt: 8,
        pb: 4,
        borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
        boxShadow: isDarkMode
          ? `0px -4px 32px ${alpha('#000', 0.4)}, 0 0 80px ${alpha(theme.palette.primary.main, 0.08)}`
          : `0px -4px 32px ${alpha(theme.palette.primary.main, 0.08)}, 0 0 80px ${alpha(theme.palette.primary.main, 0.04)}`,
        position: 'relative',
        mt: 'auto',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, transparent)`,
          opacity: 0.6,
        }
      }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={5}>
          <Grid item xs={12} md={5} lg={5}>
            <FooterSection>
              <Typography
                variant="h5"
                gutterBottom
                fontWeight="bold"
                color="primary"
                sx={{
                  fontFamily: '"Montserrat", "Open Sauce", sans-serif',
                  display: 'inline-block',
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.brandColors?.blueRightRight || '#3ed0fc'})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: '0 0 10px rgba(0,178,252,0.2)',
                  mb: 2
                }}
              >
                Value Chain Global
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph sx={{ lineHeight: 1.8, mb: 3 }}>
                Empowering investors and innovators with real-time data to identify opportunities, discover where innovation fits, and localize sectors across Saudi Arabia's diverse economic landscape.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontWeight: 500 }}>
                Website: <MuiLink href="https://value-chain.global/" target="_blank" rel="noopener" color="primary" underline="hover">https://value-chain.global/</MuiLink>
              </Typography>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600 }}>
                  Follow Us
                </Typography>
                <Stack direction="row" spacing={1.5}>
                  <SocialIconButton
                    size="medium"
                    aria-label="LinkedIn"
                    component="a"
                    href="https://www.linkedin.com/company/value-chain-global/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <LinkedIn fontSize="small" />
                  </SocialIconButton>
                  <SocialIconButton
                    size="medium"
                    aria-label="X (formerly Twitter)"
                    component="a"
                    href="https://x.com/valuechainsa"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <X fontSize="small" />
                  </SocialIconButton>
                  <SocialIconButton
                    size="medium"
                    aria-label="WhatsApp Channel"
                    component="a"
                    href="https://whatsapp.com/channel/0029VaxGmRx6GcGIw66w5M1S"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <WhatsApp fontSize="small" />
                  </SocialIconButton>
                </Stack>
              </Box>
            </FooterSection>
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={3}>
            <FooterSection>
              <Typography
                variant="h6"
                gutterBottom
                className="section-title"
                sx={{
                  fontFamily: '"Montserrat", "Open Sauce", sans-serif',
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  pb: 1,
                  mb: 2,
                  transition: 'all 0.3s ease',
                }}
              >
                Explore
              </Typography>
              <Box component="nav">
                <LinkItem href="/" color="inherit" underline="none">Home</LinkItem>
                <LinkItem href="/industries" color="inherit" underline="none">Industries</LinkItem>
                <LinkItem href="/marketplace" color="inherit" underline="none">Marketplace</LinkItem>
                <LinkItem href="/members" color="inherit" underline="none">Members</LinkItem>
                <LinkItem href="/experts" color="inherit" underline="none">Experts</LinkItem>
              </Box>
            </FooterSection>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={4}>
            <FooterSection>
              <Typography
                variant="h6"
                gutterBottom
                className="section-title"
                sx={{
                  fontFamily: '"Montserrat", "Open Sauce", sans-serif',
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  pb: 1,
                  mb: 2,
                  transition: 'all 0.3s ease',
                }}
              >
                Contact Us
              </Typography>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{
                    mr: 1.5,
                    p: 1,
                    borderRadius: 1.5,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.secondary.main, 0.08)})`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    color: theme.palette.primary.main,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                    }
                  }}>
                    <LocationOn fontSize="small" />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.6 }}>
                    Al Nemer Center No. 2<br />
                    Musa Ibn Nusair St, Al Olaya<br />
                    Riyadh 12214, Saudi Arabia
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{
                    mr: 1.5,
                    p: 1,
                    borderRadius: 1.5,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.secondary.main, 0.08)})`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    color: theme.palette.primary.main,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                    }
                  }}>
                    <Email fontSize="small" />
                  </Box>
                  <MuiLink
                    href="mailto:support@value-chain.global"
                    color="inherit"
                    underline="none"
                    sx={{
                      fontWeight: 500,
                      '&:hover': { color: theme.palette.primary.main }
                    }}
                  >
                    support@value-chain.global
                  </MuiLink>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{
                    mr: 1.5,
                    p: 1,
                    borderRadius: 1.5,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.secondary.main, 0.08)})`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    color: theme.palette.primary.main,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                    }
                  }}>
                    <Business fontSize="small" />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    CR #7050466759
                  </Typography>
                </Box>
              </Box>
            </FooterSection>
          </Grid>
        </Grid>
        
        <Divider 
          sx={{ 
            my: 5, 
            opacity: 0.1,
            '&::before, &::after': {
              borderColor: alpha(theme.palette.divider, 0.05),
            }
          }}
        />
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' }, 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ opacity: 0.8 }}
          >
            &copy; {new Date().getFullYear()} Value Chain Global. All rights reserved.
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            mt: { xs: 2, sm: 0 }, 
            gap: 3,
            '& a': {
              fontWeight: 500,
              fontSize: '0.85rem',
              transition: 'all 0.2s ease',
              '&:hover': {
                color: theme.palette.primary.main
              }
            }
          }}>
            <MuiLink href="/privacy" color="inherit" underline="none" variant="body2">
              Privacy Policy
            </MuiLink>
            <MuiLink href="/terms" color="inherit" underline="none" variant="body2">
              Terms of Service
            </MuiLink>
            <MuiLink href="/sitemap" color="inherit" underline="none" variant="body2">
              Sitemap
            </MuiLink>
          </Box>
        </Box>
      </Container>
      
      {/* Back to top button */}
      <Box 
        sx={{ 
          position: 'absolute', 
          right: 25, 
          top: -25,
          zIndex: 1
        }}
      >
        <IconButton
          onClick={scrollToTop}
          aria-label="back to top"
          sx={{
            width: 56,
            height: 56,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
            color: theme.palette.primary.contrastText,
            boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
              transform: 'translateY(-8px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.5)}`
            }
          }}
        >
          <ArrowUpward />
        </IconButton>
      </Box>
    </Box>
  );
};

export default Footer;